import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    SiteLocationBreadcrumbs,
    SiteLocationCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as SiteLocationActions from "../../../actions/siteLocations";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./SiteLocationPage.scss";

interface SiteLocationPageProps {
    form: Dtos.SiteLocation & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    siteLocation: Dtos.SiteLocation;
    lookups: Dtos.Lookup[];
    loadingSiteLocation: boolean;
    loadSiteLocationFailure: boolean;
    loadSiteLocationErrorCode: string;
    savingSiteLocation: boolean;
    saveSiteLocationSuccess: boolean;
    saveSiteLocationFailure: boolean;
    creatingSiteLocation: boolean;
    createSiteLocationSuccess: boolean;
    validatingSiteLocation: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearSiteLocation: SiteLocationActions.ISiteLocationClearActionCreator;
    loadSiteLocation: SiteLocationActions.ISiteLocationLoadByIdActionCreator;
    saveSiteLocation: SiteLocationActions.ISiteLocationSaveActionCreator;
    createSiteLocation: SiteLocationActions.ISiteLocationCreateActionCreator;
    validateSiteLocation: SiteLocationActions.ISiteLocationValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.siteLocation";

const SiteLocationPage: FC<SiteLocationPageProps> = ({
    form,
    formState,
    formProperties,
    siteLocation,
    lookups,
    loadingSiteLocation,
    loadSiteLocationFailure,
    loadSiteLocationErrorCode,
    savingSiteLocation,
    saveSiteLocationSuccess,
    saveSiteLocationFailure,
    creatingSiteLocation,
    createSiteLocationSuccess,
    validatingSiteLocation,
    validationFailures,
    permissions,
    clearSiteLocation,
    loadSiteLocation,
    saveSiteLocation,
    createSiteLocation,
    validateSiteLocation,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { siteLocationId } = useParams<{ siteLocationId: string }>();
    const siteLocationIdNumber = parseInt(siteLocationId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.SiteLocation = {} as Dtos.SiteLocation;

        loadForm(reduxFormName, Object.assign({ ...siteLocation }, extraData));
    };


    // Load site location
    useEffect(() => {
        if (siteLocationIdNumber && !siteLocation && !loadingSiteLocation) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(SiteLocationBreadcrumbs(siteLocationIdNumber));
            loadSiteLocation(siteLocationIdNumber);
        } else if (
            !siteLocationIdNumber &&
            !creatingSiteLocation &&
            !createSiteLocationSuccess
        ) {
            createSiteLocation();
            setBreadcrumbs(SiteLocationCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (siteLocation && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        siteLocationIdNumber,
        siteLocation,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        SiteLocationBreadcrumbs,
        loadSiteLocation,
        createSiteLocation,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingSiteLocation,
        creatingSiteLocation,
        createSiteLocationSuccess,
        SiteLocationCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadSiteLocationFailure) {
            navigate(`/error/${loadSiteLocationErrorCode}`);
        }
    }, [loadSiteLocationFailure, loadSiteLocationErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (siteLocationIdNumber && siteLocation?.value) {
            setBreadcrumbs(
                SiteLocationBreadcrumbs(
                    siteLocationIdNumber,
                    siteLocation?.value
                )
            );
        }
    }, [
        siteLocationIdNumber,
        siteLocation,
        setBreadcrumbs,
        SiteLocationBreadcrumbs,
    ]);

    // Reload site location after saving
    useEffect(() => {
        if (!savingSiteLocation && saveSiteLocationSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Site location saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/institution/sitelocations";
                navigate(url);
            } else if (!siteLocationIdNumber) {
                let url: string = `/settings/institution/sitelocations/${siteLocation?.id}`;
                navigate(url);
                clearSiteLocation();
            }
        }
    }, [
        savingSiteLocation,
        saveSiteLocationSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingSiteLocation && saveSiteLocationFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingSiteLocation, saveSiteLocationFailure]);

    // Cleanup effect to clear the site location on component unmount
    useEffect(() => {
        return () => {
            clearSiteLocation();
            resetForm(reduxFormName);
        };
    }, [clearSiteLocation, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/institution/sitelocations";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.SiteLocation) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.SiteLocation = {} as Dtos.SiteLocation;

            await saveSiteLocation(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.SiteLocation = {} as Dtos.SiteLocation;

            if (form && hasBeenSubmitted && !validatingSiteLocation) {
                await validateSiteLocation(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.SiteLocationsUpdate,
                Dtos.Permission.SiteLocationsCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingSiteLocation ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "siteLocation",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "siteLocation",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "siteLocation",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "siteLocation",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingSiteLocation,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingSiteLocation}
            permission={Dtos.Permission.SiteLocationsView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<SiteLocationPageProps> => {
    let siteLocation: Dtos.SiteLocation | undefined = !(
        state.siteLocations.formData instanceof Array
    )
        ? state.siteLocations.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        siteLocation: siteLocation!,
        loadingSiteLocation:
            state.siteLocations.loadState &&
            state.siteLocations.loadState.status == RequestState.Pending,
        loadSiteLocationFailure:
            state.siteLocations.loadState &&
            state.siteLocations.loadState.status == RequestState.Failure,
        loadSiteLocationErrorCode: state.siteLocations.loadState.errorCode,
        savingSiteLocation:
            state.siteLocations.saveState &&
            state.siteLocations.saveState.status == RequestState.Pending,
        saveSiteLocationSuccess:
            state.siteLocations.saveState &&
            state.siteLocations.saveState.status == RequestState.Success,
        saveSiteLocationFailure:
            state.siteLocations.saveState &&
            state.siteLocations.saveState.status == RequestState.Failure,
        creatingSiteLocation:
            state.siteLocations.createState &&
            state.siteLocations.createState.status == RequestState.Pending,
        createSiteLocationSuccess:
            state.siteLocations.createState &&
            state.siteLocations.createState.status == RequestState.Success,
        validatingSiteLocation:
            state.siteLocations.validateState &&
            state.siteLocations.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.siteLocation,
        formState: state.siteLocations.formState,
        formProperties: state.siteLocations.formProperties,
        validationFailures: siteLocation
            ? state.siteLocations.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<SiteLocationPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadSiteLocation: bindActionCreators(
            SiteLocationActions.LoadSiteLocationById,
            dispatch
        ),
        clearSiteLocation: bindActionCreators(
            SiteLocationActions.Clear,
            dispatch
        ),
        saveSiteLocation: bindActionCreators(
            SiteLocationActions.SaveSiteLocation,
            dispatch
        ),
        createSiteLocation: bindActionCreators(
            SiteLocationActions.CreateSiteLocation,
            dispatch
        ),
        validateSiteLocation: bindActionCreators(
            SiteLocationActions.ValidateSiteLocation,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SiteLocationPage);
